import { useState } from 'react'

const CATEGORY_LABELS = {
  metal: 'Metals & Alloys',
  polymer: 'Polymers & Plastics',
  biological: 'Biological Tissue',
}

const CATEGORY_ORDER = ['metal', 'polymer', 'biological']

export default function MaterialSelector({ materials, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const mat = materials[selected]

  // Build grouped list for dropdown
  const grouped = {}
  for (const [id, m] of Object.entries(materials)) {
    const cat = m.category || 'other'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push({ ...m, id })
  }

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-300 mb-1">Material</h3>
      <p className="text-[11px] text-gray-500 mb-3">What is your part made of?</p>

      {/* Selected material display / dropdown trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gray-800 border border-gray-700 hover:border-gray-500 rounded-lg p-3 text-left transition-colors"
      >
        {mat ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">{mat.icon}</span>
              <div>
                <p className="text-sm text-white font-medium">{mat.name}</p>
                <p className="text-[11px] text-gray-400">
                  Yield: {mat.yieldStrengthMpa} MPa | E: {mat.youngModulusGpa} GPa
                </p>
              </div>
            </div>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        ) : Object.keys(materials).length === 0 ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Loading materials...</span>
            <svg className="w-4 h-4 text-gray-400 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="31.4 31.4" strokeDashoffset="10" />
            </svg>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Select a material...</span>
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="mt-2 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
          {CATEGORY_ORDER.map(cat => {
            const mats = grouped[cat]
            if (!mats) return null
            return (
              <div key={cat}>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider px-3 pt-2 pb-1 bg-gray-850 sticky top-0">
                  {CATEGORY_LABELS[cat]}
                </p>
                {mats.map(m => (
                  <button
                    key={m.id}
                    onClick={() => { onChange(m.id); setIsOpen(false) }}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                      selected === m.id
                        ? 'bg-blue-600/20 text-blue-300'
                        : 'hover:bg-gray-700 text-gray-300'
                    }`}
                  >
                    <span className="text-base">{m.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{m.name}</p>
                      <p className="text-[10px] text-gray-500">
                        Yield: {m.yieldStrengthMpa} MPa | E: {m.youngModulusGpa} GPa
                      </p>
                    </div>
                    {selected === m.id && (
                      <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {/* Selected material properties */}
      {mat && !isOpen && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <PropertyCard label="Young's Modulus" value={`${mat.youngModulusGpa} GPa`} hint="Stiffness" />
          <PropertyCard label="Yield Strength" value={`${mat.yieldStrengthMpa} MPa`} hint="Max stress before deformation" />
          <PropertyCard label="Poisson's Ratio" value={mat.poissonRatio} hint="Lateral stretch" />
          <PropertyCard label="Density" value={`${mat.densityKgM3} kg/m³`} hint="Weight per volume" />
        </div>
      )}
    </div>
  )
}

function PropertyCard({ label, value, hint }) {
  return (
    <div className="bg-gray-800 rounded p-2">
      <p className="text-[10px] text-gray-500">{label}</p>
      <p className="text-xs text-white font-medium">{value}</p>
      <p className="text-[9px] text-gray-600">{hint}</p>
    </div>
  )
}
